import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoginForm } from "@/components/auth/LoginForm";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { Layout } from "@/components/layout/Layout";
import { useAuth } from "@/hooks/use-auth";
import { Building2 } from "lucide-react";

export default function AuthPage() {
  const [activeTab, setActiveTab] = useState<string>("login");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  
  // Get tab and role from URL query parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam === "login" || tabParam === "register") {
      setActiveTab(tabParam);
    }

    // If coming from "Register as Agent" or "Register as Owner" links
    if (tabParam === "register") {
      const role = params.get("role");
      if (role === "agent" || role === "owner") {
        // Pass the role info to RegisterForm component
        const event = new CustomEvent("selectRole", { 
          detail: { role }
        });
        document.dispatchEvent(event);
      }
    }
  }, []);
  
  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update URL query parameter
    const url = new URL(window.location.href);
    url.searchParams.set("tab", value);
    window.history.pushState({}, "", url);
  };

  return (
    <Layout>
      <div className="container py-10 md:py-16">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          {/* Form section */}
          <div className="space-y-6">
            
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight">
                {activeTab === "login" ? "Welcome back" : "Create your account"}
              </h1>
              <p className="text-muted-foreground">
                {activeTab === "login" 
                  ? "Sign in to your account to continue" 
                  : "Fill in the details below to create your account"}
              </p>
            </div>
            
            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="login">Login</TabsTrigger>
                <TabsTrigger value="register">Register</TabsTrigger>
              </TabsList>
              
              <TabsContent value="login" className="space-y-6">
                <LoginForm />
                
                <div className="text-center text-sm text-muted-foreground">
                  Don't have an account?{" "}
                  <button
                    onClick={() => handleTabChange("register")}
                    className="font-medium text-primary hover:underline"
                  >
                    Create one
                  </button>
                </div>
              </TabsContent>
              
              <TabsContent value="register" className="space-y-6">
                <RegisterForm />
                
                <div className="text-center text-sm text-muted-foreground">
                  Already have an account?{" "}
                  <button
                    onClick={() => handleTabChange("login")}
                    className="font-medium text-primary hover:underline"
                  >
                    Sign in
                  </button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Hero section */}
          <div className="relative hidden md:block">
            <div className="rounded-lg bg-muted p-8 h-full">
              <div className="flex flex-col justify-between h-full space-y-6">
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold">Join our premium property rental marketplace</h2>
                  <p className="text-muted-foreground">
                    StayMiddle connects property owners with agents and travelers for
                    a seamless rental experience in Dubai.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <svg
                        className="h-4 w-4 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium">For Property Owners</h3>
                      <p className="text-sm text-muted-foreground">
                        List your properties, manage bookings, and sync calendars with other platforms
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <svg
                        className="h-4 w-4 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-medium">For Rental Agents</h3>
                      <p className="text-sm text-muted-foreground">
                        Create booking links, set custom markups, and track commissions
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                      <svg
                        className="h-4 w-4 text-primary"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    {/* <div className="space-y-1">
                      <h3 className="font-medium">For Travelers</h3>
                      <p className="text-sm text-muted-foreground">
                        Find and book premium properties with transparent pricing
                      </p>
                    </div> */}
                  </div>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  By signing up, you agree to our{" "}
                  <a href="/terms" className="text-primary hover:underline">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" className="text-primary hover:underline">
                    Privacy Policy
                  </a>
                  .
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}